package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.milta.core.auth.JwtParser
import com.milta.core.auth.OAuthSignInClient
import com.milta.core.auth.SessionEvent
import com.milta.core.auth.TokenStorage
import com.milta.core.cache.LocalCacheStore
import com.milta.core.localization.LocalStrings
import com.milta.core.localization.ProvideAppStrings
import com.milta.core.localization.resolveLanguageTag
import com.milta.core.mode.AppMode
import com.milta.core.mode.AppModeManager
import com.milta.di.adminModule
import com.milta.di.appConfigModule
import com.milta.di.authModule
import com.milta.di.deliveryModule
import com.milta.di.importJobsModule
import com.milta.di.importsModule
import com.milta.di.jsModule
import com.milta.di.legalModule
import com.milta.di.modeModule
import com.milta.di.networkModule
import com.milta.di.optimizationQuotaModule
import com.milta.di.aiCreditModule
import com.milta.di.profileModule
import com.milta.di.routeModule
import com.milta.di.spreadsheetImportModule
import com.milta.di.subscriptionModule
import com.milta.di.teamModule
import com.milta.feature.auth.domain.entity.OAuthAuthenticateOutcome
import com.milta.feature.auth.domain.entity.OAuthProvider
import com.milta.feature.auth.domain.entity.UserRole
import com.milta.feature.auth.domain.repository.AuthRepository
import com.milta.feature.admin.domain.usecase.GetAdminAccountsUseCase
import com.milta.feature.admin.presentation.AdminUsersViewModel
import com.milta.feature.legal.domain.entity.LegalDocumentType
import com.milta.feature.optimizationquota.presentation.OptimizationQuotaStore
import com.milta.feature.aicredit.presentation.AiCreditStore
import com.milta.feature.profile.domain.usecase.ConfirmAccountDeletionUseCase
import com.milta.feature.profile.domain.usecase.ConfirmPhoneChangeUseCase
import com.milta.feature.profile.domain.usecase.GetMyDriverLinkCodeUseCase
import com.milta.feature.profile.domain.usecase.GetProfileUseCase
import com.milta.feature.profile.domain.usecase.RequestAccountDeletionUseCase
import com.milta.feature.profile.domain.usecase.RequestPhoneChangeUseCase
import com.milta.feature.profile.domain.usecase.UpdateLanguageUseCase
import com.milta.feature.profile.presentation.ProfileViewModel
import com.milta.feature.team.domain.usecase.GetMyEnterpriseUseCase
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.renderComposable
import org.koin.compose.koinInject
import org.koin.core.context.startKoin

/**
 * `:webApp` (Kotlin/JS, Compose HTML) entry point. Bootstraps Koin ([jsModule] for the
 * web-specific `TokenStorage`/`OAuthSignInClient`/etc., [networkModule]/[authModule]/[modeModule]/
 * [profileModule] shared with mobile) then renders the auth cascade (Welcome → EditPhone → Otp), mirroring
 * `MainActivity`'s state machine for the same screens — written fresh here rather than shared,
 * since `MainActivity` also owns ~20 mobile-only route/import screens that don't apply to the web
 * dashboard, and its screens are Compose UI (canvas), incompatible with Compose HTML's DOM-based
 * composables. Only Google is wired end-to-end; Apple shows a "coming soon" message (see
 * `docs/TODO.md`). Web is Enterprise/Admin-only and login-only (decision 2026-08-17, extended
 * 2026-10-02 for Admin): there is no account-creation form on web — a Google account with no
 * matching Milta account gets a "not found" error and stays on Welcome, and `requireWebAccess`
 * signs a Driver-only account back out rather than letting it reach [Screen.Dashboard]. Admin has
 * nothing to do with Enterprise (no tenant, no subscription) — it renders a separate
 * [Screen.AdminDashboard] with an empty main nav (see [DashboardShell]'s `active = null`), never
 * [Screen.Dashboard]'s Enterprise-only content.
 */
fun main() {
    startKoin {
        // MIL-50 — routeModule/teamModule/spreadsheetImportModule added for the "Ajouter une
        // tournée" flow (WebImportRouteViewModel: import + driver assignment + dispatch).
        // importsModule added (2026-08-20) so the same flow can also accept PDF files
        // (ExtractDeliveriesFromPdfUseCase, POST /imports/pdf — same use case already wired for
        // mobile, see WebImportRouteViewModel). subscriptionModule added (MIL-53) for the
        // Paramètres page's read-only abonnement card (GetMySubscriptionUseCase/GetSubscriptionPlansUseCase).
        // appConfigModule — RoutaR kill switch (ad hoc feature, 2026-08), shared with mobile.
        modules(jsModule, networkModule, authModule, modeModule, profileModule, routeModule, deliveryModule, teamModule, subscriptionModule, spreadsheetImportModule, importsModule, importJobsModule, appConfigModule, legalModule, adminModule, optimizationQuotaModule, aiCreditModule)
    }

    // Public pages (/privacy, /terms, /delete-account — Google Play) are picked before App() is ever
    // composed: no session check, no role gate, no redirect to Welcome, signed in or not (see
    // PublicRoute's doc).
    val publicRoute = PublicRoute.parse(window.location.pathname)
    renderComposable(rootElementId = "root") {
        if (publicRoute != null) PublicPage(publicRoute) else App()
    }
}

// Web dashboard has no signed-in-user "current language" until authenticated (ProfileState is
// post-login only, see ProfileViewModel), so the pre-login language chip needs its own persistence
// — mirrors JsAppModeStorage's `localStorage` key pattern.
private const val LANGUAGE_STORAGE_KEY = "milta_web_language"

private sealed interface Screen {
    data object Welcome : Screen
    data class EditPhone(val provider: OAuthProvider, val idToken: String) : Screen
    data class Otp(val phone: String, val previousScreen: Screen = Welcome) : Screen
    data object Dashboard : Screen
    // Admin skeleton (2026-10-02) — deliberately separate from Dashboard: that branch fires
    // Enterprise-only calls on mount (GetMyEnterprise, team/import/history view models), which
    // would 403 immediately for an Admin account.
    data object AdminDashboard : Screen
}

/**
 * The 3 destinations of the Admin main nav (2026-10-03) — deliberately its own enum, not
 * [DashboardDestination]: Admin has nothing to do with Enterprise's tenant-scoped destinations
 * (see [Screen.AdminDashboard]'s doc). Labels are literal French, not piped through
 * [com.milta.core.localization.LocalStrings] (CommonStrings/the 9 translation files) — an
 * Eonix-internal screen, never shown to a Milta client, same reasoning as the "Admin" role label.
 */
private enum class AdminDestination(val label: String, val icon: String) {
    USAGE_IA("Usage IA", "📊"),
    CONSO_IA("Conso. IA", "⚡"),
    PROMPTS("Prompts", "📝"),
    UTILISATEURS("Utilisateurs", "👤"),
    // MIL-97 — global usage limits (tab "Optimisations RoutaR"; "Tokens IA" to come).
    // "🚦", not "🎚": the latter has no glyph on most systems and left an empty box in the menu.
    LIMITES_USAGE("Limites d'usage", "🚦"),
}

/**
 * URL scheme for the authenticated dashboard (DOM-06 — browser back/forward + refresh/deep-link
 * support, no external router: `kotlinx.browser.window.history`/`window.onpopstate` directly, see
 * [App]'s doc). Deliberately NOT wired to the pre-login cascade (`Screen.Welcome`/`EditPhone`/`Otp`)
 * — that linear flow stays exactly as before, unrouted.
 *
 * `historiqueRouteId` is only meaningful for [DashboardDestination.HISTORIQUE_DES_PREUVES] — MIL-52's
 * Screen B (`/dashboard/history/{routeId}`) vs Screen A (`/dashboard/history`). URL segments are
 * deliberately English (`dashboard`/`routes`/`import`/`team`/`history`/`settings`), independent of
 * the interface's own selected display language — same convention as most multi-language web apps
 * (URLs stay stable/shareable regardless of which of the 10 UI languages is active).
 */
private object DashboardRoute {
    private const val BASE = "dashboard"

    data class Parsed(
        val destination: DashboardDestination,
        val showSettings: Boolean = false,
        val historiqueRouteId: String? = null,
        /** `/dashboard/legal/terms` or `/dashboard/legal/privacy` — see [WebLegalContent]. Always
         * reached from Paramètres (MIL-17), independent of [showSettings]/[destination] below. */
        val legalType: LegalDocumentType? = null,
    )

    /** Anything unrecognized (root `/`, unknown path) defaults to Tournées du jour — same fallback
     * used for a freshly authenticated session with no prior dashboard URL (see [App]'s
     * `requireEnterprise`). */
    fun parse(pathname: String): Parsed {
        val segments = pathname.trim('/').split('/').filter { it.isNotBlank() }
        if (segments.firstOrNull() != BASE) return Parsed(DashboardDestination.TOURNEES_DU_JOUR)
        return when (segments.getOrNull(1)) {
            "routes" -> Parsed(DashboardDestination.TOURNEES_DU_JOUR)
            "import" -> Parsed(DashboardDestination.AJOUTER_UNE_TOURNEE)
            "team" -> Parsed(DashboardDestination.EQUIPE)
            "history" -> Parsed(DashboardDestination.HISTORIQUE_DES_PREUVES, historiqueRouteId = segments.getOrNull(2))
            "settings" -> Parsed(DashboardDestination.TOURNEES_DU_JOUR, showSettings = true)
            "legal" -> Parsed(
                DashboardDestination.TOURNEES_DU_JOUR,
                legalType = when (segments.getOrNull(2)) {
                    "terms" -> LegalDocumentType.TermsOfUse
                    "privacy" -> LegalDocumentType.PrivacyPolicy
                    else -> null
                },
            )
            else -> Parsed(DashboardDestination.TOURNEES_DU_JOUR)
        }
    }

    fun encode(
        destination: DashboardDestination,
        showSettings: Boolean = false,
        historiqueRouteId: String? = null,
        legalType: LegalDocumentType? = null,
    ): String {
        if (legalType != null) {
            val slug = if (legalType == LegalDocumentType.TermsOfUse) "terms" else "privacy"
            return "/$BASE/legal/$slug"
        }
        if (showSettings) return "/$BASE/settings"
        return when (destination) {
            DashboardDestination.TOURNEES_DU_JOUR -> "/$BASE/routes"
            DashboardDestination.AJOUTER_UNE_TOURNEE -> "/$BASE/import"
            DashboardDestination.EQUIPE -> "/$BASE/team"
            DashboardDestination.HISTORIQUE_DES_PREUVES ->
                if (historiqueRouteId != null) "/$BASE/history/$historiqueRouteId" else "/$BASE/history"
        }
    }
}

@Composable
private fun App() {
    val localCacheStore: LocalCacheStore = koinInject()
    var language by remember {
        val savedLanguage = localCacheStore.getString(LANGUAGE_STORAGE_KEY)
        mutableStateOf(resolveLanguageTag(savedLanguage ?: window.navigator.language))
    }
    var screen by remember { mutableStateOf<Screen>(Screen.Welcome) }
    var oauthError by remember { mutableStateOf<String?>(null) }
    var activeDestination by remember { mutableStateOf(DashboardDestination.TOURNEES_DU_JOUR) }
    // MIL-53 — pinned outside DashboardDestination (see DashboardShell's enum doc); hoisted up here
    // (rather than local to the `Screen.Dashboard` branch, as before DOM-06) so both the initial
    // URL parse and the `window.onpopstate` handler below can read/write it directly.
    var showSettings by remember { mutableStateOf(false) }
    // DOM-06 — Main.kt's belief of the URL's `/dashboard/historique/{routeId}` segment; see
    // WebHistoriqueContent's `routeIdFromUrl` doc for the full bidirectional wiring.
    var historiqueRouteId by remember { mutableStateOf<String?>(null) }
    // MIL-17/legal — Main.kt's belief of the URL's `/dashboard/legal/{terms|privacy}` segment,
    // reached from Paramètres' "Mentions légales" card (see WebLegalContent's doc). Independent of
    // [showSettings] above: set together on navigation (see [onNavigateToTerms]/[onNavigateToPrivacy]
    // below), so "back" from the legal page returns to Paramètres.
    var legalDocumentType by remember { mutableStateOf<LegalDocumentType?>(null) }
    // MIL-50/MIL-51 — carries the already-localized confirmation message across the "Ajouter une
    // tournée" → "Tournées du jour" navigation on a successful dispatch (WebImportRouteContent's
    // ViewModel is discarded once we navigate away, so it can't outlive the switch itself; the
    // message itself — singular driver name or multi-driver count, MIL-51 — is composed by
    // WebImportRouteContent, which still has access to the batch result at that point). Cleared as
    // soon as the manager navigates elsewhere so it never reappears on a later, unrelated visit to
    // Tournées du jour.
    var dispatchSuccessMessage by remember { mutableStateOf<String?>(null) }

    val repository: AuthRepository = koinInject()
    val oauthClient: OAuthSignInClient = koinInject()
    val tokenStorage: TokenStorage = koinInject()
    val appModeManager: AppModeManager = koinInject()
    val optimizationQuotaStore: OptimizationQuotaStore = koinInject()
    val aiCreditStore: AiCreditStore = koinInject()
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        if (repository.isAuthenticated()) {
            val role = tokenStorage.getAccessToken()?.let(JwtParser::extractRole)
            if (role == UserRole.ENTERPRISE.apiValue) {
                appModeManager.setModeFromRole(role)
                // DOM-06 — a page refresh or a directly-opened/bookmarked dashboard URL restores
                // the exact screen it points to instead of always defaulting to Tournées du jour.
                val parsed = DashboardRoute.parse(window.location.pathname)
                activeDestination = parsed.destination
                showSettings = parsed.showSettings
                historiqueRouteId = parsed.historiqueRouteId
                legalDocumentType = parsed.legalType
                screen = Screen.Dashboard
            } else if (role == UserRole.ADMIN.apiValue) {
                // No AppMode/DashboardRoute concept for Admin — no tenant, no nested destinations.
                screen = Screen.AdminDashboard
            } else {
                repository.logout()
            }
        }
        repository.sessionEvents.collect { event ->
            if (event is SessionEvent.SessionExpired || event is SessionEvent.LoggedOut) {
                screen = Screen.Welcome
                // DOM-06 — the URL is otherwise left on whatever /dashboard/... path was active
                // (pushState survives independently of Compose state): the Welcome screen's own
                // resource loading (logo/OAuth icons/flags, none of which render anywhere inside
                // the routed Dashboard) then resolves relative to that stale nested path and 404s.
                // Reset to root so Welcome always loads its assets against the real site root.
                window.history.replaceState(null, "", "/")
                // Bug fix — a dispatch confirmation banner otherwise survives a logout/re-login
                // cycle in the same tab (this composable is never disposed) and resurfaces on
                // Tournées du jour for a completely unrelated session. Clear it here alongside the
                // other per-session UI state.
                dispatchSuccessMessage = null
                // MIL-97 — never show the previous account's optimization counter.
                optimizationQuotaStore.clear()
                // MIL-98 — never show nor use the previous account's AI credit.
                aiCreditStore.clear()
            }
        }
    }

    // DOM-06 — browser back/forward across the whole dashboard nav, registered only while
    // Dashboard is showing (a `null` pre-login screen is intentionally left unrouted, see
    // DashboardRoute's doc). Only ever reads the URL and updates in-memory state — it must NEVER
    // itself call pushState/replaceState, or back/forward would get "stuck" re-pushing the very
    // entry it just popped.
    DisposableEffect(screen) {
        if (screen is Screen.Dashboard) {
            window.onpopstate = {
                val parsed = DashboardRoute.parse(window.location.pathname)
                activeDestination = parsed.destination
                showSettings = parsed.showSettings
                historiqueRouteId = parsed.historiqueRouteId
                legalDocumentType = parsed.legalType
            }
        }
        onDispose { window.onpopstate = null }
    }

    ProvideAppStrings(languageTag = language) {
        val strings = LocalStrings.current

        // Web dashboard is Enterprise/Admin-only — Drivers use the mobile app (decision
        // 2026-08-15, extended 2026-10-02 for Admin). A Driver-only account reaching here (direct
        // login or post-OTP) is signed back out immediately instead of landing on a dashboard.
        suspend fun requireWebAccess(role: String) {
            when (role) {
                UserRole.ENTERPRISE.apiValue -> {
                    appModeManager.setModeFromRole(role)
                    // DOM-06 — a fresh login has no prior dashboard URL to restore (unlike the
                    // refresh/deep-link path above) — reset to the default screen and reflect it in
                    // the URL. `replaceState`, not `pushState`: there is nothing meaningful to go
                    // "back" to on the pre-login cascade (out of scope, see DashboardRoute's doc),
                    // so this shouldn't add its own history entry.
                    activeDestination = DashboardDestination.TOURNEES_DU_JOUR
                    showSettings = false
                    historiqueRouteId = null
                    legalDocumentType = null
                    window.history.replaceState(null, "", DashboardRoute.encode(DashboardDestination.TOURNEES_DU_JOUR))
                    screen = Screen.Dashboard
                }
                UserRole.ADMIN.apiValue -> {
                    // No AppMode/DashboardRoute concept for Admin — no tenant, no nested destinations.
                    screen = Screen.AdminDashboard
                }
                else -> {
                    repository.logout()
                    oauthError = strings.common.oauthDriverAccountNotAllowedOnWeb
                    screen = Screen.Welcome
                }
            }
        }

        when (val s = screen) {
            Screen.Welcome -> WebWelcomeScreen(
                selectedLanguage = language,
                onLanguageChange = {
                    language = it
                    localCacheStore.putString(LANGUAGE_STORAGE_KEY, it)
                },
                onOAuthSignIn = { provider ->
                    if (provider == OAuthProvider.APPLE) {
                        oauthError = strings.common.oauthAppleWebComingSoon
                    } else {
                        oauthError = null
                        scope.launch {
                            runCatching { oauthClient.signIn(provider) }
                                .onSuccess { result ->
                                    repository.authenticateWithOAuth(provider, result.idToken)
                                        .onSuccess { outcome ->
                                            when (outcome) {
                                                // Web never creates accounts (decision 2026-08-17):
                                                // only an existing Enterprise account can log in
                                                // here — an unregistered Google account gets a
                                                // clear error instead of a sign-up form.
                                                OAuthAuthenticateOutcome.NotRegistered ->
                                                    oauthError = strings.common.oauthAccountNotFoundOnWeb
                                                is OAuthAuthenticateOutcome.PhoneVerificationRequired ->
                                                    screen = Screen.Otp(
                                                        outcome.phoneNumber,
                                                        previousScreen = Screen.EditPhone(provider, result.idToken),
                                                    )
                                                is OAuthAuthenticateOutcome.Authenticated ->
                                                    requireWebAccess(outcome.role)
                                            }
                                        }
                                        // Never surface e.message: raw exceptions carry technical detail
                                        // (see MIL-15 scénario 3 — message générique obligatoire).
                                        .onFailure { oauthError = strings.common.oauthErrorGeneric }
                                }
                                .onFailure { oauthError = strings.common.oauthErrorGeneric }
                        }
                    }
                },
                errorMessage = oauthError,
            )

            is Screen.EditPhone -> WebEditPhoneScreen(
                provider = s.provider,
                idToken = s.idToken,
                onBack = { screen = Screen.Welcome },
                onUpdated = { phone -> screen = Screen.Otp(phone, previousScreen = s) },
            )

            is Screen.Otp -> WebOtpScreen(
                phone = s.phone,
                onBack = { screen = s.previousScreen },
                onVerified = { role -> scope.launch { requireWebAccess(role) } },
            )

            Screen.Dashboard -> {
                val getProfile: GetProfileUseCase = koinInject()
                val updateLanguage: UpdateLanguageUseCase = koinInject()
                val requestPhoneChange: RequestPhoneChangeUseCase = koinInject()
                val confirmPhoneChange: ConfirmPhoneChangeUseCase = koinInject()
                val requestAccountDeletion: RequestAccountDeletionUseCase = koinInject()
                val confirmAccountDeletion: ConfirmAccountDeletionUseCase = koinInject()
                val getMyDriverLinkCode: GetMyDriverLinkCodeUseCase = koinInject()
                val profileViewModel = remember {
                    ProfileViewModel(getProfile, updateLanguage, requestPhoneChange, confirmPhoneChange, requestAccountDeletion, confirmAccountDeletion, getMyDriverLinkCode)
                }
                val profileState by profileViewModel.state.collectAsState()
                val appMode by appModeManager.mode.collectAsState()

                // MIL-53 — GET /enterprises/me now exists: unlocks the sidebar's organisation badge
                // (previously always null, see DashboardShell doc) and feeds the Paramètres header.
                val getMyEnterprise: GetMyEnterpriseUseCase = koinInject()
                var organizationName by remember { mutableStateOf<String?>(null) }
                LaunchedEffect(Unit) {
                    getMyEnterprise().onSuccess { organizationName = it.name }
                }

                val headerTitle = when {
                    legalDocumentType == LegalDocumentType.TermsOfUse -> strings.profile.menuTerms
                    legalDocumentType == LegalDocumentType.PrivacyPolicy -> strings.profile.menuPrivacy
                    showSettings -> strings.common.navParametres
                    else -> when (activeDestination) {
                        DashboardDestination.TOURNEES_DU_JOUR -> strings.common.navTourneesDuJour
                        DashboardDestination.AJOUTER_UNE_TOURNEE -> strings.common.navAjouterUneTournee
                        DashboardDestination.EQUIPE -> strings.team.screenTitle
                        DashboardDestination.HISTORIQUE_DES_PREUVES -> strings.common.navHistoriqueDesPreuves
                    }
                }
                val userDisplayName = "${profileState.firstName} ${profileState.lastName}".trim()

                // MIL-50 — seule "Ajouter une tournée" a un sous-titre d'en-tête ; MIL-53 — le nom
                // de l'organisation sert de sous-titre sur Paramètres (voir maquette). MIL-17 — pas
                // de sous-titre sur la page d'un document légal.
                val headerSubtitle = when {
                    legalDocumentType != null -> null
                    showSettings -> organizationName
                    activeDestination == DashboardDestination.AJOUTER_UNE_TOURNEE -> strings.webImportRoute.headerSubtitle
                    else -> null
                }

                DashboardShell(
                    active = activeDestination,
                    headerTitle = headerTitle,
                    headerSubtitle = headerSubtitle,
                    organizationName = organizationName,
                    userDisplayName = userDisplayName.ifBlank { "…" },
                    // MIL-48 — "Gestionnaire" on web only (sidebar footer), deliberately not
                    // strings.profile.roleOrganisation ("Entreprise"/"Enterprise"): that key is
                    // shared with the mobile ProfileScreen and stays untouched (see CommonStrings.sidebarRoleManagerLabel doc).
                    userRoleLabel = if (appMode == AppMode.DRIVER) strings.profile.roleDriver else strings.common.sidebarRoleManagerLabel,
                    settingsActive = showSettings,
                    onNavigate = { destination ->
                        if (destination != DashboardDestination.TOURNEES_DU_JOUR) dispatchSuccessMessage = null
                        showSettings = false
                        legalDocumentType = null
                        activeDestination = destination
                        // DOM-06 — a sidebar click always lands on Historique's Screen A, never
                        // resumes a previously-open Screen B (matches the mockup's "top-level nav
                        // item" semantics) — clear it, don't just leave a stale nested id in place.
                        historiqueRouteId = null
                        // Bug fix — re-clicking the already-active item (or Historique while on its
                        // Screen B, which does need to collapse back to Screen A above) must not
                        // push a second, identical history entry: only push when the target path
                        // actually differs from the current one, or Back needs an extra press to
                        // leave the dashboard at all.
                        val path = DashboardRoute.encode(destination)
                        if (window.location.pathname != path) window.history.pushState(null, "", path)
                    },
                    onSettingsClick = {
                        // Bug fix — Paramètres is "elsewhere" per TourneesDuJourContent's own doc
                        // comment, same as any other sidebar destination, but this handler is
                        // separate from `onNavigate` and was never clearing the banner.
                        dispatchSuccessMessage = null
                        showSettings = true
                        legalDocumentType = null
                        // Bug fix — see onNavigate: don't push a duplicate entry when Paramètres is
                        // clicked again while already open.
                        val path = DashboardRoute.encode(activeDestination, showSettings = true)
                        if (window.location.pathname != path) window.history.pushState(null, "", path)
                    },
                    onLogout = { scope.launch { repository.logout() } },
                ) {
                    val currentLegalType = legalDocumentType
                    if (currentLegalType != null) {
                        // MIL-17 — "back" always returns to Paramètres (the only entry point, see
                        // WebSettingsContent's LegalCard), never a plain browser history.back().
                        WebLegalContent(
                            type = currentLegalType,
                            onBack = {
                                legalDocumentType = null
                                val path = DashboardRoute.encode(activeDestination, showSettings = true)
                                if (window.location.pathname != path) window.history.pushState(null, "", path)
                            },
                        )
                    } else if (showSettings) {
                        WebSettingsContent(
                            onLanguageChanged = {
                                language = it
                                localCacheStore.putString(LANGUAGE_STORAGE_KEY, it)
                            },
                            onOrganizationDeleted = { scope.launch { repository.logout() } },
                            onNavigateToTerms = {
                                legalDocumentType = LegalDocumentType.TermsOfUse
                                val path = DashboardRoute.encode(activeDestination, legalType = LegalDocumentType.TermsOfUse)
                                if (window.location.pathname != path) window.history.pushState(null, "", path)
                            },
                            onNavigateToPrivacy = {
                                legalDocumentType = LegalDocumentType.PrivacyPolicy
                                val path = DashboardRoute.encode(activeDestination, legalType = LegalDocumentType.PrivacyPolicy)
                                if (window.location.pathname != path) window.history.pushState(null, "", path)
                            },
                        )
                    } else {
                        // MIL-50/MIL-51 — le succès de l'envoi (POST /route-batches, Écran 3, puis
                        // POST /route-batches/{id}/dispatch) ramène sur "Tournées du jour".
                        when (activeDestination) {
                            DashboardDestination.AJOUTER_UNE_TOURNEE -> WebImportRouteContent(
                                onDispatched = { confirmationMessage ->
                                    dispatchSuccessMessage = confirmationMessage
                                    activeDestination = DashboardDestination.TOURNEES_DU_JOUR
                                    // DOM-06 — replaceState, not pushState: after a successful
                                    // dispatch there's nothing useful to go "back" to on
                                    // /dashboard/import (the wizard's own state is gone, the
                                    // ViewModel gets discarded right here), same "redirect after
                                    // submit" reasoning as an HTTP 303.
                                    window.history.replaceState(null, "", DashboardRoute.encode(DashboardDestination.TOURNEES_DU_JOUR))
                                },
                            )
                            DashboardDestination.TOURNEES_DU_JOUR -> TourneesDuJourContent(
                                dispatchSuccessMessage = dispatchSuccessMessage,
                            )
                            DashboardDestination.EQUIPE -> WebTeamContent()
                            DashboardDestination.HISTORIQUE_DES_PREUVES -> WebHistoriqueContent(
                                routeIdFromUrl = historiqueRouteId,
                                onSelectedRouteChanged = { newRouteId ->
                                    // No-op whenever this merely reports back a value Main.kt itself
                                    // just pushed down (deep link / browser back-forward, see
                                    // WebHistoriqueContent's doc) — only a genuine in-page
                                    // open/back click reaches here with a value that doesn't match
                                    // yet, which is exactly what must be pushed onto the URL.
                                    if (newRouteId != historiqueRouteId) {
                                        historiqueRouteId = newRouteId
                                        window.history.pushState(
                                            null,
                                            "",
                                            DashboardRoute.encode(DashboardDestination.HISTORIQUE_DES_PREUVES, historiqueRouteId = newRouteId),
                                        )
                                    }
                                },
                            )
                        }
                    }
                }
            }

            // 4 real nav destinations + Paramètres + Se déconnecter, all wired to a real backend:
            // Utilisateurs (AdminUsersContent, GET /api/v1/admin/accounts), Prompts
            // (AdminPromptsContent, GET/PUT /api/v1/admin/prompts), Usage IA (AdminOcrAnalysisContent,
            // GET /api/v1/admin/ocr-analysis — the background OCR analysis pipeline's cost-comparison
            // view), Conso. IA (AdminClaudeUsageContent, GET /api/v1/admin/claude-usage — the real
            // production ClaudeUsageEvent tracking, formerly described as a future idea in
            // docs/admin-role-dashboard-requirements.md in the milta repo, now implemented). Never
            // reuses Screen.Dashboard's content (see Screen.AdminDashboard's doc).
            Screen.AdminDashboard -> {
                val getProfile: GetProfileUseCase = koinInject()
                var firstName by remember { mutableStateOf("") }
                var lastName by remember { mutableStateOf("") }
                LaunchedEffect(Unit) {
                    getProfile().onSuccess {
                        firstName = it.firstName
                        lastName = it.lastName
                    }
                }
                var adminDestination by remember { mutableStateOf<AdminDestination?>(AdminDestination.USAGE_IA) }
                var adminSettingsActive by remember { mutableStateOf(false) }
                // No DashboardRoute/URL deep-link concept for Admin (see Screen.AdminDashboard's
                // doc) — a plain local remember is enough, unlike legalDocumentType above.
                var adminLegalType by remember { mutableStateOf<LegalDocumentType?>(null) }
                // MIL-97 — "Fiche utilisateur" opened from Utilisateurs; null = the list itself.
                var adminAccountId by remember { mutableStateOf<String?>(null) }
                // Hoisted here (not inside AdminUsersContent): the search and the expanded
                // organisations are kept when coming back from the Fiche.
                val getAdminAccounts: GetAdminAccountsUseCase = koinInject()
                val adminUsersViewModel = remember { AdminUsersViewModel(getAdminAccounts) }
                val userDisplayName = "$firstName $lastName".trim()

                DashboardShell(
                    active = null,
                    headerTitle = when {
                        adminLegalType == LegalDocumentType.TermsOfUse -> strings.profile.menuTerms
                        adminLegalType == LegalDocumentType.PrivacyPolicy -> strings.profile.menuPrivacy
                        adminSettingsActive -> strings.common.navParametres
                        else -> adminDestination?.label.orEmpty()
                    },
                    // "Détails de l'utilisateur" under "Utilisateurs" while an account is open (mockup B3).
                    headerSubtitle = strings.optimizationQuota.accountDetailsTitle.takeIf {
                        adminAccountId != null && !adminSettingsActive && adminLegalType == null && adminDestination == AdminDestination.UTILISATEURS
                    },
                    organizationName = null,
                    userDisplayName = userDisplayName.ifBlank { "…" },
                    // Not piped through i18n (CommonStrings/the 9 translation files): an
                    // Eonix-internal account label, never shown to a Milta client.
                    userRoleLabel = "Admin",
                    settingsActive = adminSettingsActive,
                    onNavigate = {},
                    onSettingsClick = { adminSettingsActive = true; adminLegalType = null; adminAccountId = null },
                    onLogout = { scope.launch { repository.logout() } },
                    mainNav = {
                        AdminDestination.entries.forEach { destination ->
                            NavItem(destination.label, destination.icon, !adminSettingsActive && adminDestination == destination) {
                                adminSettingsActive = false
                                adminLegalType = null
                                adminAccountId = null
                                adminDestination = destination
                            }
                        }
                    },
                ) {
                    val currentAdminLegalType = adminLegalType
                    val currentAdminAccountId = adminAccountId
                    when {
                        // MIL-17 — "back" always returns to Paramètres (the only entry point, see
                        // AdminSettingsContent's LegalCard), mirrors the Dashboard branch's own
                        // WebLegalContent wiring above.
                        currentAdminLegalType != null -> WebLegalContent(
                            type = currentAdminLegalType,
                            onBack = { adminLegalType = null },
                        )
                        adminSettingsActive -> AdminSettingsContent(
                            onLanguageChanged = {
                                language = it
                                localCacheStore.putString(LANGUAGE_STORAGE_KEY, it)
                            },
                            onNavigateToTerms = { adminLegalType = LegalDocumentType.TermsOfUse },
                            onNavigateToPrivacy = { adminLegalType = LegalDocumentType.PrivacyPolicy },
                        )
                        // Backend ready (GET /api/v1/admin/accounts, /admin/prompts, /admin/ocr-analysis).
                        adminDestination == AdminDestination.UTILISATEURS && currentAdminAccountId != null ->
                            AdminAccountQuotaContent(
                                accountId = currentAdminAccountId,
                                listLabel = AdminDestination.UTILISATEURS.label,
                                onBack = { adminAccountId = null },
                            )
                        adminDestination == AdminDestination.UTILISATEURS -> AdminUsersContent(
                            onOpenAccount = { adminAccountId = it },
                            hoistedViewModel = adminUsersViewModel,
                        )
                        adminDestination == AdminDestination.LIMITES_USAGE -> AdminUsageLimitsContent()
                        adminDestination == AdminDestination.PROMPTS -> AdminPromptsContent()
                        adminDestination == AdminDestination.USAGE_IA -> AdminOcrAnalysisContent()
                        adminDestination == AdminDestination.CONSO_IA -> AdminClaudeUsageContent()
                        else -> Div { Text("${adminDestination?.label} — à venir") }
                    }
                }
            }
        }
    }
}

/** "Tournées du jour" (MIL-48, DOM-06 — Supervision, see [WebDashboardContent]) — shows a one-off
 * success banner right above it just after a MIL-50/MIL-51 dispatch, using the same
 * [InlineBanner]/[InlineBannerKind.Success] the "Vérifier & assigner" screen's other inline banners
 * use, rather than a toast: Compose HTML has no snackbar host wired on the web dashboard.
 * [dispatchSuccessMessage] is already fully localized/formatted by [WebImportRouteContent] (single
 * driver name or multi-driver count, MIL-51) — nothing left to compose here. */
@Composable
private fun TourneesDuJourContent(dispatchSuccessMessage: String?) {
    Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("height", "100%") } }) {
        if (dispatchSuccessMessage != null) {
            InlineBanner(kind = InlineBannerKind.Success, text = dispatchSuccessMessage)
        }
        Div(attrs = { style { property("flex", "1"); property("min-height", "0") } }) {
            WebDashboardContent()
        }
    }
}

